import React, { useState, useMemo } from "react";
import { Table, Form, Button, InputGroup, Badge, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatNumber, formatPercentage, formatRelativeTime, getSuccessRateColorClass } from "../../../utils/formatUtils";
import { TbSearch, TbDownload, TbArrowUp, TbArrowDown, TbEye } from "react-icons/tb";
import "./SuperUserAnalytics.css";

function ClientsTable({ clients, loading, onExportCSV }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "totalExecutions",
    direction: "desc",
  });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-muted ms-1" style={{ opacity: 0.3 }}>↕</span>;
    }
    return sortConfig.direction === "asc" ? (
      <TbArrowUp className="ms-1" size={14} />
    ) : (
      <TbArrowDown className="ms-1" size={14} />
    );
  };

  const filteredAndSortedClients = useMemo(() => {
    if (!clients) return [];

    let filtered = clients.filter((client) =>
      client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.clientId.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = -Infinity;
      if (bVal === null || bVal === undefined) bVal = -Infinity;

      // String comparison for client names
      if (sortConfig.key === "clientName" || sortConfig.key === "clientId") {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [clients, searchTerm, sortConfig]);

  const handleViewClient = (client) => {
    navigate(`/analytics/client/${client.clientId}`, {
      state: {
        clientName: client.clientName,
        clientId: client.clientId
      }
    });
  };

  if (loading) {
    return (
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Analytics by Client</h5>
          </div>
        </Card.Header>
        <Card.Body className="placeholder-glow">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="placeholder col-12 mb-2" style={{ height: "40px" }}></div>
          ))}
        </Card.Body>
      </Card>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">Analytics by Client</h5>
        </Card.Header>
        <Card.Body className="text-center py-5">
          <p className="text-muted">No client data available for the selected date range.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">Analytics by Client</h5>
          <div className="d-flex gap-2">
            <InputGroup style={{ maxWidth: "300px" }}>
              <InputGroup.Text>
                <TbSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button variant="outline-secondary" size="sm" onClick={onExportCSV}>
              <TbDownload className="me-1" />
              Export CSV
            </Button>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th
                  onClick={() => handleSort("clientName")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Client Name {getSortIcon("clientName")}
                </th>
                <th
                  onClick={() => handleSort("totalExecutions")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                  className="text-end"
                >
                  Total Executions {getSortIcon("totalExecutions")}
                </th>
                <th
                  onClick={() => handleSort("successRate")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                  className="text-end"
                >
                  Success Rate {getSortIcon("successRate")}
                </th>
                <th
                  onClick={() => handleSort("totalApiCalls")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                  className="text-end"
                >
                  API Calls {getSortIcon("totalApiCalls")}
                </th>
                <th
                  onClick={() => handleSort("lastExecutionTime")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Last Execution {getSortIcon("lastExecutionTime")}
                </th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedClients.map((client) => (
                <tr key={client.clientId}>
                  <td>
                    <div>
                      <strong>{client.clientName}</strong>
                      <div className="text-muted small">{client.clientId}</div>
                    </div>
                  </td>
                  <td className="text-end">
                    {formatNumber(client.totalExecutions)}
                    <div className="small text-muted">
                      <span className="text-success">{formatNumber(client.successfulExecutions)}</span>
                      {" / "}
                      <span className="text-danger">{formatNumber(client.failedExecutions)}</span>
                    </div>
                  </td>
                  <td className="text-end">
                    <Badge
                      bg={
                        client.successRate >= 95
                          ? "success"
                          : client.successRate >= 85
                          ? "warning"
                          : "danger"
                      }
                    >
                      {formatPercentage(client.successRate)}
                    </Badge>
                  </td>
                  <td className="text-end">{formatNumber(client.totalApiCalls)}</td>
                  <td>
                    {client.lastExecutionTime ? (
                      <span className="text-muted small">
                        {formatRelativeTime(client.lastExecutionTime)}
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td className="text-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleViewClient(client)}
                    >
                      <TbEye className="me-1" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
      {filteredAndSortedClients.length === 0 && searchTerm && (
        <Card.Footer className="text-center text-muted">
          No clients found matching "{searchTerm}"
        </Card.Footer>
      )}
      {filteredAndSortedClients.length > 0 && (
        <Card.Footer className="text-muted small">
          Showing {filteredAndSortedClients.length} of {clients.length} client{clients.length !== 1 ? 's' : ''}
        </Card.Footer>
      )}
    </Card>
  );
}

export default ClientsTable;
